import React, { useEffect, useMemo, useState } from "react";
import CheckInView from "./checkin.view";
import api from "../../api/axiosConfig";
import { SidebarItem } from "../../components/Center Admin/layout/Sidebar";
import "../../center admin css/centerAdmin.css";

/* ---------- Types ---------- */
interface Appointment {
  _id: string;
  child_id: string;
  status: "Booked" | "Cancelled" | "Completed";
  booked_on: string;
  appointmentTime?: string;
  child_name: string;
  child_age: number;
  child_gender?: string;
  child_profile?: string;
}

interface CheckInQueueItem {
  _id: string;
  createdAt: string;
  pathId?: string;
  child: {
    name: string;
    age: number;
    profile?: string;
  };
  meta?: {
    slotLabel?: string;
  };
}

interface CheckInQueueResponse {
  waiting: CheckInQueueItem[];
}

export interface CheckInSidebarItem extends SidebarItem {
  fullData: any;
}

/* ---------- Helpers ---------- */
const formatDateForAPI = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/** Cached user shape */
type CachedUser = {
  displayName?: string;
  roleAssignments?: Array<
    | {
        roleId?: { name?: string };
        name?: string;
        centerId?: string;
      }
    | any
  >;
  centerAssignments?: Array<{ centerId?: string }>;
};

function readCachedUser(): CachedUser | null {
  const keys = ["backendResponse", "CurrrentResponse", "CurrentResponse"];
  for (const k of keys) {
    try {
      const raw = sessionStorage.getItem(k);
      if (raw) return JSON.parse(raw);
    } catch {}
  }
  return null;
}

function deriveGreeting(u: CachedUser | null): string {
  if (!u) return "User";
  if (u.displayName?.trim()) return u.displayName;
  const firstRole = u.roleAssignments?.[0];
  return firstRole?.roleId?.name || firstRole?.name || "User";
}


function getCenterId(): string | null {
  return sessionStorage.getItem("centerId");
}


/* ---------- Component ---------- */
const CheckInPage: React.FC = () => {
  const [greetingName, setGreetingName] = useState(() =>
    deriveGreeting(readCachedUser()),
  );

  const [appointments, setAppointments] = useState<CheckInSidebarItem[]>([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedEmrId, setSelectedEmrId] = useState<string | null>(null);
  const [childData, setChildData] = useState<any>(null);
  const [parentData, setParentData] = useState<any>(null);

  useEffect(() => {
    const onStorage = () => setGreetingName(deriveGreeting(readCachedUser()));
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("accessToken");
    if (!token) {
      setError("No access token. Please log in.");
      setLoading(false);
      return;
    }

    // const centerId = getCenterId(readCachedUser());
    const centerId = getCenterId();
    if (!centerId) {
      setError("No center id found.");
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const dateParam = formatDateForAPI(selectedDate);

        await api.centerworkflowApi.post(
          `/workflow/appointments/import/center/${encodeURIComponent(
            centerId,
          )}/date?date=${encodeURIComponent(dateParam)}&include_cancelled=true`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const url = `/queues/CHECKIN_Q?center_id=${encodeURIComponent(
          centerId,
        )}&date=${encodeURIComponent(dateParam)}`;

        const { data } = await api.centerworkflowApi.get<CheckInQueueResponse>(
          url,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const bookings = data?.waiting || [];

        const list: CheckInSidebarItem[] = bookings
          .sort(
            (a: CheckInQueueItem, b: CheckInQueueItem) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
          )
          .map((appt: CheckInQueueItem) => ({
            key: appt._id,
            label: appt.child?.name,
            subLabel: `${appt.child?.age} Years | Path: ${appt.pathId || "Unassigned"}`,

            time: appt.meta?.slotLabel,
            avatarUrl: appt.child?.profile,
            fullData: appt,
          }));

        setAppointments(list);
        if (!list.length) setError("No appointments found for this date.");
      } catch (err) {
        console.error(err);
        setError("No appointments found for this date.");
        setAppointments([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [selectedDate]);

  useEffect(() => {
    if (!selectedEmrId) return;
    const child = appointments.find(
      (a) => a.fullData.child_id === selectedEmrId,
    );
    if (child) {
      setChildData(child.fullData);
      setParentData({ name: "Parent" });
    }
  }, [selectedEmrId, appointments]);

  const userName = useMemo(() => greetingName, [greetingName]);

  return (
    <CheckInView
      items={appointments}
      isLoading={loading}
      error={error}
      selectedDate={selectedDate}
      onDateChange={setSelectedDate}
      userName={userName}
      onEmrSelect={setSelectedEmrId}
      childData={childData}
      parentData={parentData}
    />
  );
};

export default CheckInPage;
